import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Shield, Clock, DollarSign, CheckCircle } from 'lucide-react';
import { useVisibility } from '../hooks/useVisibility';

const PaymentAuthorizationModal = ({ authorization, onApprove, onCancel, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [approving, setApproving] = useState(false);
  const isVisible = useVisibility();

  useEffect(() => {
    if (!authorization) return undefined;

    const calculateTimeLeft = () => {
      const expiresAt = new Date(authorization.expires_at);
      const now = new Date();
      const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeLeft(diff);
    };

    calculateTimeLeft();
    if (!isVisible) return undefined;
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [authorization, isVisible]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRiskColor = (score) => {
    if (score >= 7) return 'text-red-400 bg-red-500/20 border-red-500/30';
    if (score >= 4) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    return 'text-green-400 bg-green-500/20 border-green-500/30';
  };

  const getRiskLabel = (score) => {
    if (score >= 7) return 'High Risk';
    if (score >= 4) return 'Medium Risk';
    return 'Low Risk';
  };

  const handleApprove = async () => {
    if (timeLeft === 0) {
      alert('Authorization has expired');
      return;
    }
    setApproving(true);
    try {
      await onApprove(authorization.authorization_id);
    } finally {
      setApproving(false);
    }
  };

  const handleCancel = async () => {
    const reason = prompt('Reason for cancellation:');
    if (!reason) return;
    await onCancel(authorization.authorization_id, reason);
  };

  if (!authorization) return null;

  const isExpired = timeLeft === 0;
  const isAlmostExpired = timeLeft > 0 && timeLeft <= 60;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="payment-auth-modal">
      <div className="bg-[#1C1C1E] border-2 border-[#F59E0B] rounded-lg max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#F59E0B] p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-black" />
            <div>
              <h2 className="text-2xl font-bold text-black">Payment Authorization</h2>
              <p className="text-black/80 text-sm">Review and approve payment request</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg" data-testid="close-modal">
            <X className="w-6 h-6 text-black" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className={`p-4 rounded-lg border ${isExpired ? 'bg-red-500/20 border-red-500/30' : isAlmostExpired ? 'bg-yellow-500/20 border-yellow-500/30 animate-pulse' : 'bg-blue-500/20 border-blue-500/30'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className={`w-5 h-5 ${isExpired ? 'text-red-400' : isAlmostExpired ? 'text-yellow-400' : 'text-blue-400'}`} />
                <span className={`font-medium ${isExpired ? 'text-red-400' : isAlmostExpired ? 'text-yellow-400' : 'text-blue-400'}`}>
                  {isExpired ? 'EXPIRED' : 'Time Remaining'}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isExpired ? 'text-red-400' : isAlmostExpired ? 'text-yellow-400' : 'text-white'}`} data-testid="timer">
                {isExpired ? '0:00' : formatTime(timeLeft)}
              </div>
            </div>
          </div>

          <div className="bg-[#0A0A0A] rounded-lg p-5 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-gray-700">
              <span className="text-gray-400">Amount</span>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-2xl font-bold text-white" data-testid="amount">
                  ${authorization.amount.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400">Payee</span>
              <span className="text-white font-medium" data-testid="payee">{authorization.payee}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400">Source Account</span>
              <span className="text-white font-medium" data-testid="source-account">{authorization.source_account}</span>
            </div>

            <div className="flex items-start justify-between pt-3 border-t border-gray-700">
              <span className="text-gray-400">Reason</span>
              <span className="text-white text-right max-w-xs" data-testid="reason">{authorization.reason}</span>
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${getRiskColor(authorization.risk_score)}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Risk Assessment</span>
              </div>
              <span className="text-lg font-bold" data-testid="risk-score">{authorization.risk_score.toFixed(1)}/10</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-black/30 rounded-full h-2">
                <div className="bg-current h-2 rounded-full" style={{ width: `${(authorization.risk_score / 10) * 100}%` }} />
              </div>
              <span className="text-sm font-medium">{getRiskLabel(authorization.risk_score)}</span>
            </div>
          </div>

          {authorization.requires_2fa && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-400 font-medium mb-1">2FA Required</p>
                  <p className="text-blue-300/80">This payment requires two-factor authentication. <span className="font-semibold">(Stub - not implemented yet)</span></p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-yellow-400 font-medium mb-1">Authorization Only</p>
                <p className="text-yellow-300/80">This is <strong>approval structure only</strong>. No actual payment will be executed.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 p-6 flex gap-3">
          <button onClick={handleCancel} className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold" data-testid="cancel-btn">Cancel</button>
          <button onClick={handleApprove} disabled={isExpired || approving} className="flex-1 px-6 py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="approve-btn">
            <CheckCircle className="w-5 h-5" />
            {approving ? 'Approving...' : isExpired ? 'Expired' : 'Approve Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentAuthorizationModal;
